import React, { useState, useCallback, useEffect } from "react";
import {
  Plus, Eye, EyeOff, Copy, Check, Pencil, Trash2,
  X, Save, AlertTriangle, KeyRound, Zap, MessageSquare,
  ShoppingCart, Globe, ChevronRight,
} from "lucide-react";
import axios from "axios";
import "../css/APIKeys.css";

/* ─────────────────────────────────────────────────────────────
   DEFAULT GROUPS & KEYS
   Replace initialValue with real values fetched from your API
───────────────────────────────────────────────────────────── */
const DEFAULT_GROUPS = [
  {
    id: "razorpay",
    label: "Razorpay",
    description: "Payment gateway credentials",
    icon: <Zap size={16} />,
    keys: [
      { id: "rp_key_id",     label: "Key ID",      hint: "rzp_live_...",    isSecret: false, value: "" },
      { id: "rp_key_secret", label: "Key Secret",   hint: "••••••••••••••", isSecret: true,  value: "" },
    ],
  },
  {
    id: "msg91",
    label: "MSG91",
    description: "SMS & OTP service",
    icon: <MessageSquare size={16} />,
    keys: [
      { id: "msg91_auth",       label: "Auth Key",         hint: "xxxxxxxxxxxxxx", isSecret: true,  value: "" },
      { id: "msg91_sender_id",  label: "Sender ID",        hint: "MOTOBD",         isSecret: false, value: "" },
      { id: "msg91_otp_temp",   label: "OTP Template ID",  hint: "6564abc...",     isSecret: false, value: "" },

    ],
  },
//   {
//     id: "google",
//     label: "Google",
//     description: "Maps, Analytics & OAuth",
//     icon: <Globe size={16} />,
//     keys: [
//       { id: "google_maps",     label: "Maps API Key",    hint: "AIza...",   isSecret: true,  value: "" },
//       { id: "google_oauth_id", label: "OAuth Client ID", hint: "xxxx.apps.googleusercontent.com", isSecret: false, value: "" },
//       { id: "google_oauth_sc", label: "OAuth Secret",    hint: "GOCSPX-...", isSecret: true, value: "" },
//     ],
//   },
//   {
//     id: "shiprocket",
//     label: "Shiprocket",
//     description: "Shipping & logistics",
//     icon: <ShoppingCart size={16} />,
//     keys: [
//       { id: "sr_email",    label: "Account Email", hint: "admin@yourcompany.com", isSecret: false, value: "" },
//       { id: "sr_password", label: "Password",      hint: "••••••••",              isSecret: true,  value: "" },
//       { id: "sr_token",    label: "API Token",     hint: "eyJhbGci...",           isSecret: true,  value: "" },
//     ],
//   },
];

/* ─────────────────────────────────────────────────────────────
   SMALL REUSABLE COMPONENTS
───────────────────────────────────────────────────────────── */
const Toast = ({ toast }) => {
  if (!toast) return null;
  return (
    <div className={`apikey-toast ${toast.type}`}>
      {toast.type === "success" ? <Check size={15} /> : <AlertTriangle size={15} />}
      {toast.message}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────────────────────── */
const ApiKeys = () => {
  //const [groups, setGroups] = useState(DEFAULT_GROUPS);
  const [groups, setGroups] = useState([]);
    const [newKey, setNewKey] = useState({

    groupId: "",

    label: "",

    hint: "",

    isSecret: true,

    value: ""

});

  useEffect(() => {
    loadGroups();
}, []);

const loadGroups = async () => {
    try {

        const res = await axios.get("/api/api-keys");

        const mapped = res.data.data.map(group => ({

            id: group.group_id,

            label: group.label,

            description: group.description,

            keys: group.keys.map(key => ({

                id: key.id,

                label: key.label,

                hint: key.hint,

                isSecret: key.is_secret,

                value: key.value

            }))

        }));

        setGroups(mapped);
         // Set the default group for the "Add Key" modal
    if (mapped.length > 0) {
      setNewKey((prev) => ({
        ...prev,
        groupId: mapped[0].id,
      }));
    }

    } catch (err) {

        console.error(err);

        showToast("Unable to load API Keys", "error");

    }
};

  // which key ids are revealed
  const [revealed, setRevealed] = useState({});
  // which key ids are in edit mode
  const [editing, setEditing] = useState({});
  // draft values while editing
  const [drafts, setDrafts] = useState({});
  // copied state per key
  const [copied, setCopied] = useState({});

  // modal: add new key
  const [showAddModal, setShowAddModal] = useState(false);


  // delete confirm
  const [deleteTarget, setDeleteTarget] = useState(null); // { groupId, keyId, label }

  // toast
  const [toast, setToast] = useState(null);

  /* ── helpers ── */
  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 2800);
  };

  const toggleReveal = (keyId) =>
    setRevealed((p) => ({ ...p, [keyId]: !p[keyId] }));

  const handleCopy = (keyId, value) => {
    if (!value) return showToast("Nothing to copy", "error");
    navigator.clipboard.writeText(value).then(() => {
      setCopied((p) => ({ ...p, [keyId]: true }));
      showToast("Copied to clipboard");
      setTimeout(() => setCopied((p) => ({ ...p, [keyId]: false })), 2000);
    });
  };

  const startEdit = (keyId, currentValue) => {
    setEditing((p) => ({ ...p, [keyId]: true }));
    setDrafts((p) => ({ ...p, [keyId]: currentValue }));
  };

  const cancelEdit = (keyId) => {
    setEditing((p) => ({ ...p, [keyId]: false }));
    setDrafts((p) => { const n = { ...p }; delete n[keyId]; return n; });
  };

 const saveEdit = async (groupId, keyId) => {

    try {

        await axios.put(`/api/api-keys/${keyId}`, {

            value: drafts[keyId]

        });

        setGroups(prev =>
            prev.map(group =>
                group.id !== groupId
                    ? group
                    : {
                          ...group,
                          keys: group.keys.map(key =>
                              key.id !== keyId
                                  ? key
                                  : {
                                        ...key,
                                        value: drafts[keyId]
                                    }
                          )
                      }
            )
        );

        setEditing(prev => ({
            ...prev,
            [keyId]: false
        }));

        showToast("Key updated");

    } catch (err) {

        console.error(err);

        showToast("Update failed", "error");

    }

};

  const confirmDelete = async () => {

    if (!deleteTarget) return;

    try {

        await axios.delete(`/api/api-keys/${deleteTarget.keyId}`);

        await loadGroups();

        setDeleteTarget(null);

        showToast("Key deleted");

    } catch (err) {

        console.error(err);

        showToast("Delete failed", "error");

    }

};

 const handleAddKey = async () => {

    if (!newKey.label.trim()) {

        return showToast("Label required", "error");

    }

    try {

        const payload = {

            id: `custom_${Date.now()}`,

            group_id: newKey.groupId,

            label: newKey.label,

            hint: newKey.hint,

            is_secret: newKey.isSecret,

            value: newKey.value

        };

        await axios.post("/api/api-keys", payload);

        await loadGroups();

        setShowAddModal(false);

        setNewKey({

            groupId: groups[0]?.id || "",

            label: "",

            hint: "",

            isSecret: true,

            value: ""

        });

        showToast("Key Added");

    } catch (err) {

        console.error(err);

        showToast("Unable to add key", "error");

    }

};
  /* ── MASK VALUE ── */
  const maskValue = (val) => {
    if (!val) return "";
    if (val.length <= 8) return "•".repeat(val.length);
    return val.slice(0, 4) + "•".repeat(Math.min(val.length - 8, 16)) + val.slice(-4);
  };

  return (
    <div className="apikeys-page">

      {/* ── HEADER ── */}
      <div className="apikeys-header">
        <div>
          <h1>API Key Management</h1>
          <p>Store and manage all third-party service credentials</p>
        </div>
       {/* <button className="add-key-btn" onClick={() => setShowAddModal(true)}>
          <Plus size={15} />
          Add New Key
        </button>*/}
      </div>

      {/* ── GROUPS ── */}
      <div className="apikeys-groups">
        {groups.map((group) => (
          <div key={group.id} className="apikey-group">

            {/* Group header */}
            <div className="apikey-group-header">
              <div className="group-icon">{group.icon}</div>
              <div>
                <h2>{group.label}</h2>
                <p>{group.description}</p>
              </div>
            </div>

            {/* Keys */}
            {group.keys.length === 0 ? (
              <div className="apikey-group-empty">No keys in this group yet.</div>
            ) : (
              group.keys.map((k) => {
                const isEditing  = !!editing[k.id];
                const isRevealed = !!revealed[k.id];
                const isCopied   = !!copied[k.id];
                const displayVal = isEditing
                  ? drafts[k.id]
                  : k.isSecret && !isRevealed
                    ? maskValue(k.value)
                    : k.value;

                return (
                  <div key={k.id} className="apikey-row">

                    {/* Label */}
                    <div className="apikey-row-label">
                      <strong>{k.label}</strong>
                      <span>{k.hint}</span>
                    </div>

                    {/* Input */}
                    <div className="apikey-value-wrap">
                      <input
                        type={k.isSecret && !isRevealed && !isEditing ? "password" : "text"}
                        value={displayVal}
                        readOnly={!isEditing}
                        placeholder={isEditing ? k.hint : k.value ? "" : "Not set"}
                        className={isEditing ? "editing" : ""}
                        onChange={(e) =>
                          setDrafts((p) => ({ ...p, [k.id]: e.target.value }))
                        }
                      />
                    </div>

                    {/* Actions */}
                    <div className="apikey-row-actions">

                      {/* Reveal toggle — only for secret keys */}
                      {k.isSecret && !isEditing && (
                        <button
                          className="icon-btn"
                          title={isRevealed ? "Hide" : "Reveal"}
                          onClick={() => toggleReveal(k.id)}
                        >
                          {isRevealed ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                      )}

                      {/* Copy */}
                      {!isEditing && (
                        <button
                          className={`icon-btn ${isCopied ? "copied" : ""}`}
                          title="Copy"
                          onClick={() => handleCopy(k.id, k.value)}
                        >
                          {isCopied ? <Check size={14} /> : <Copy size={14} />}
                        </button>
                      )}

                      {/* Edit / Save / Cancel */}
                      {isEditing ? (
                        <>
                          <button className="icon-btn save" title="Save" onClick={() => saveEdit(group.id, k.id)}>
                            <Save size={14} />
                          </button>
                          <button className="icon-btn" title="Cancel" onClick={() => cancelEdit(k.id)}>
                            <X size={14} />
                          </button>
                        </>
                      ) : (
                        <button className="icon-btn" title="Edit" onClick={() => startEdit(k.id, k.value)}>
                          <Pencil size={14} />
                        </button>
                      )}

                      {/* Delete */}
                      {!isEditing && (
                        <button
                          className="icon-btn danger"
                          title="Delete"
                          onClick={() => setDeleteTarget({ groupId: group.id, keyId: k.id, label: k.label })}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}

                    </div>
                  </div>
                );
              })
            )}
          </div>
        ))}
      </div>

      {/* ── ADD KEY MODAL ── */}
      {showAddModal && (
        <div className="apikey-modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="apikey-modal" onClick={(e) => e.stopPropagation()}>

            <div className="apikey-modal-header">
              <h2>Add New Key</h2>
              <button className="modal-close-btn" onClick={() => setShowAddModal(false)}>
                <X size={16} />
              </button>
            </div>

            <div className="apikey-modal-body">

              <div className="form-field">
                <label>Group</label>
                <select
                  value={newKey.groupId}
                  onChange={(e) => setNewKey((p) => ({ ...p, groupId: e.target.value }))}
                >
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>{g.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-field">
                <label>Label</label>
                <input
                  type="text"
                  placeholder="e.g. Webhook Secret"
                  value={newKey.label}
                  onChange={(e) => setNewKey((p) => ({ ...p, label: e.target.value }))}
                />
              </div>

              <div className="form-field">
                <label>Placeholder / Hint</label>
                <input
                  type="text"
                  placeholder="e.g. whsec_..."
                  value={newKey.hint}
                  onChange={(e) => setNewKey((p) => ({ ...p, hint: e.target.value }))}
                />
              </div>

              <div className="form-field">
                <label>Value</label>
                <input
                  type="text"
                  className="mono-input"
                  placeholder="Paste your key here"
                  value={newKey.value}
                  onChange={(e) => setNewKey((p) => ({ ...p, value: e.target.value }))}
                />
              </div>

              <div className="form-field">
                <label>Type</label>
                <select
                  value={newKey.isSecret ? "secret" : "public"}
                  onChange={(e) => setNewKey((p) => ({ ...p, isSecret: e.target.value === "secret" }))}
                >
                  <option value="secret">Secret (masked by default)</option>
                  <option value="public">Public (visible)</option>
                </select>
              </div>

            </div>

            <div className="apikey-modal-footer">
              <button className="btn-cancel" onClick={() => setShowAddModal(false)}>Cancel</button>
              <button className="btn-save" onClick={handleAddKey} disabled={!newKey.label.trim()}>
                Add Key
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ── DELETE CONFIRM MODAL ── */}
      {deleteTarget && (
        <div className="apikey-modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="delete-confirm-modal" onClick={(e) => e.stopPropagation()}>

            <div className="delete-confirm-body">
              <div className="danger-icon">
                <AlertTriangle size={22} />
              </div>
              <h3>Delete Key?</h3>
              <p>
                This will permanently remove <strong>{deleteTarget.label}</strong>. This action cannot be undone.
              </p>
            </div>

            <div className="delete-confirm-footer">
              <button className="btn-confirm-cancel" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button className="btn-confirm-delete" onClick={confirmDelete}>Delete</button>
            </div>

          </div>
        </div>
      )}

      {/* ── TOAST ── */}
      <Toast toast={toast} />

    </div>
  );
};

export default ApiKeys;